import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, Package, Bike } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 5000);
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setOrder(res.data);
    } catch (error) {
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !order) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div></div>;

  const statuses = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered'];
  const currentStatusIndex = statuses.indexOf(order.status);
  const deliveryLat = order.delivery_address?.lat || 28.6139;
  const deliveryLng = order.delivery_address?.lng || 77.2090;
  const driverLat = order.driver_location?.lat || deliveryLat + 0.005;
  const driverLng = order.driver_location?.lng || deliveryLng + 0.005;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full"><ArrowLeft className="w-6 h-6" /></button>
            <h1 className="text-2xl font-bold">Track Order</h1>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="font-bold text-lg mb-4">Order Status</h2>
          <div className="flex justify-between items-center mb-6">
            {statuses.map((status, idx) => (
              <div key={status} className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx <= currentStatusIndex ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {idx === 3 ? <Bike className="w-5 h-5" /> : <Package className="w-5 h-5" />}
                </div>
                <p className="text-xs mt-2 text-center">{status.replace('_', ' ')}</p>
                {idx < statuses.length - 1 && <div className={`h-1 w-full ${idx < currentStatusIndex ? 'bg-blue-600' : 'bg-gray-200'}`} style={{ position: 'relative', top: '-30px', zIndex: -1 }}></div>}
              </div>
            ))}
          </div>
        </div>
        {order.status === 'out_for_delivery' && (
          <div className="bg-white rounded-xl shadow-md p-4 mb-6">
            <h2 className="font-bold text-lg mb-4">Live Tracking</h2>
            <MapContainer center={[deliveryLat, deliveryLng]} zoom={14} style={{ height: '400px', borderRadius: '12px' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap contributors' />
              <Marker position={[deliveryLat, deliveryLng]}><Popup>Delivery Location</Popup></Marker>
              <Marker position={[driverLat, driverLng]}><Popup>Delivery Partner</Popup></Marker>
            </MapContainer>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="font-bold text-lg mb-4">Order Details</h2>
          <div className="space-y-2">
            {order.items.map((item, idx) => <div key={idx} className="flex justify-between"><span>{item.name} x {item.quantity}</span><span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span></div>)}
            <div className="border-t pt-2 flex justify-between font-bold"><span>Total</span><span className="text-blue-600">₹{order.total_amount.toFixed(2)}</span></div>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default OrderTrackingPage;
